import React, { useEffect, useState } from "react";

interface ImageProps {
  /**
   * The source URL of the image
   */
  src: string;
  /**
   * Alt text for accessibility and fallback display
   */
  alt: string;
  /**
   * Optional width of the image
   */
  width?: number;
  /**
   * Optional height of the image
   */
  height?: number;
  /**
   * Optional className for custom styling
   */
  className?: string;
  /**
   * Optional loading priority
   */
  priority?: boolean;
}

/**
 * A reusable image component with loading states and error handling
 * @param props - ImageProps
 * @returns React component
 */
export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  className = "",
  priority = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
    >
      {isLoading && (
        <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%]" />
      )}
      {hasError ? (
        <div className="flex h-full w-full items-center justify-center bg-gray-100 p-4 text-center">
          <span className="text-sm text-gray-500">{alt}</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`block h-full w-full object-cover transition-opacity duration-300 ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? "eager" : "lazy"}
          width={width}
          height={height}
        />
      )}
    </div>
  );
};
